/**
 * 로그 조회
 * ---------------------------------------------------------------------------
 * 같은 화면을 두 곳에서 쓴다.
 *
 *   기업 관리  `/app/logs`        자사 기록만 (`/api/logs`)
 *   시스템 관리 `/app/admin-logs` 전 기업 기록 (`/api/admin/logs`)
 *
 * 범위는 props 로 받는다. 화면이 스스로 고를 수 있게 하면 "전체로 바꿔볼까"
 * 가 가능해지고, 그건 서버가 막더라도 애초에 만들 이유가 없는 유혹이다.
 *
 * 왜 탭마다 열이 다른가
 *   로그는 종류마다 답하는 질문이 다르다. 감사로그는 "누가 무엇을 바꿨나",
 *   로그인은 "어디서 몇 번 실패했나", 발송 이력은 "왜 안 갔나" 다. 열을
 *   통일하면 셋 다 못 읽는 표가 된다.
 *
 * 기간을 먼저 고르게 하는 이유
 *   로그는 금방 수백만 줄이 된다. 기본값은 최근 7일이고 서버가 92일까지만
 *   받는다. "전체 기간" 이라는 선택지는 두지 않는다.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import { CodeTag, useCodes, type Codes } from '../../lib/codes';

const { Text, Paragraph } = Typography;

export type LogScope = 'TENANT' | 'PLATFORM';

interface LogSource {
  key: string;
  label: string;
  filters: string[];
}

interface LogPage {
  source: string;
  items: Array<Record<string, unknown>>;
  page: number;
  size: number;
  total: number;
  totalCapped: boolean;
}

interface TenantOption {
  tenantId: string;
  companyName: string;
}

interface ProductOption {
  productCode: string;
  productName: string;
}

interface RetentionStatus {
  code: string;
  label: string;
  days: number;
  minDays: number;
  rows: number | null;
  oldest: string | null;
  cutoff: string;
}

/* =========================================================================
 * 값 표시
 * ====================================================================== */

/**
 * 이 화면이 쓰는 코드 그룹.
 *
 * 표시명과 색은 서버의 공통코드가 정한다(「시스템 관리 > 공통코드」).
 * 예전에는 여기에 배열로 박혀 있었고, 같은 목록이 대시보드·기업 관리 화면과
 * 조금씩 달랐다.
 */
const CODE_GROUPS = [
  'AUDIT_CATEGORY',
  'AUDIT_RESULT',
  'ACTOR_TYPE',
  'LOGIN_RESULT',
  'LOGIN_METHOD',
  'SESSION_END_REASON',
  'MESSAGE_CHANNEL',
  'MESSAGE_STATUS',
  'ALERT_SEVERITY',
  'ALERT_STATUS',
  'PRESIGN_OPERATION',
  'WEBHOOK_RESULT',
];

/** 탭별 거르기 칸. 목록은 공통코드에서 오고, 여기서는 어느 그룹인지만 정한다. */
const FILTER_FIELDS: Record<string, Array<{ key: string; label: string; group: string }>> = {
  audit: [
    { key: 'result', label: '결과', group: 'AUDIT_RESULT' },
    { key: 'actorType', label: '행위자', group: 'ACTOR_TYPE' },
  ],
  login: [{ key: 'result', label: '결과', group: 'LOGIN_RESULT' }],
  session: [{ key: 'loginMethod', label: '방식', group: 'LOGIN_METHOD' }],
  message: [
    { key: 'channel', label: '채널', group: 'MESSAGE_CHANNEL' },
    { key: 'status', label: '상태', group: 'MESSAGE_STATUS' },
  ],
  alert: [
    { key: 'severity', label: '등급', group: 'ALERT_SEVERITY' },
    { key: 'status', label: '상태', group: 'ALERT_STATUS' },
  ],
  file: [{ key: 'operation', label: '구분', group: 'PRESIGN_OPERATION' }],
  webhook: [{ key: 'processResult', label: '처리', group: 'WEBHOOK_RESULT' }],
};

function when(value: unknown): string {
  return value ? dayjs(value as string).format('YYYY-MM-DD HH:mm:ss') : '';
}

function bytes(value: unknown): string {
  const n = Number(value ?? 0);
  if (!n) return '0';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/** 행위자 한 칸. 자사 조회는 이름이 오고, 전체 조회는 오지 않는다. */
function actor(row: Record<string, unknown>): React.ReactNode {
  const name = row.actorName as string | undefined;
  const id = row.actor_user_id as string | null;
  const type = row.actor_type as string | undefined;

  if (name) return <Text>{name}</Text>;
  if (id) {
    return (
      <Tooltip title={id}>
        <Text type="secondary" style={{ fontFamily: 'monospace' }}>
          {id.slice(0, 8)}
        </Text>
      </Tooltip>
    );
  }
  return <Text type="secondary">{type === 'SYSTEM' ? '시스템' : '—'}</Text>;
}

/* =========================================================================
 * 탭별 열
 * ====================================================================== */

type ColumnFactory = (
  scope: LogScope,
  codes: Codes,
) => ColumnsType<Record<string, unknown>>;

const COMPANY_COLUMN = {
  title: '기업',
  dataIndex: 'company_name',
  width: 150,
  render: (v: unknown) => (v as string) ?? <Text type="secondary">—</Text>,
};

/**
 * 어느 제품에서 난 기록인가.
 *
 * WORK&CRM·WORK&ECO 는 자기 일을 하면서 플랫폼 API 를 거치고, 그때 기록에
 * 자기 제품 코드가 붙는다. 이 칸이 없으면 같은 「동작」이 여러 줄 떴을 때
 * 어느 제품에서 난 것인지 알 수 없다.
 *
 * 이름은 서버가 제품 카탈로그에서 붙여 준다(product_name). 카탈로그에 없는
 * 코드면 코드를 그대로 보여 준다 — 지워진 제품의 지난 기록이 그렇다.
 */
const PRODUCT_COLUMN = {
  title: '발생 시스템',
  dataIndex: 'product_name',
  width: 140,
  render: (v: unknown, row: Record<string, unknown>) =>
    (v as string) ?? (row.product_code as string) ?? <Text type="secondary">—</Text>,
};

/** 코드 태그 한 칸. 표시명과 색이 모두 공통코드에서 온다. */
const codeColumn = (
  title: string,
  dataIndex: string,
  group: string,
  width: number,
  codes: Codes,
) => ({
  title,
  dataIndex,
  width,
  render: (v: unknown) => <CodeTag codes={codes} group={group} value={v as string | null} />,
});

const COLUMNS: Record<string, ColumnFactory> = {
  audit: (scope, codes) => [
    { title: '일시', dataIndex: 'at', width: 160, render: when },
    ...(scope === 'PLATFORM' ? [COMPANY_COLUMN] : []),
    PRODUCT_COLUMN,
    codeColumn('분류', 'category', 'AUDIT_CATEGORY', 100, codes),
    { title: '동작', dataIndex: 'action', width: 190 },
    { title: '행위자', width: 130, render: (_, row) => actor(row) },
    {
      title: '대상',
      render: (_, row) => (
        <Text>
          {(row.resource_label as string) ?? (row.resource_type as string) ?? ''}
          {row.resource_id ? (
            <Text type="secondary"> ({String(row.resource_id).slice(0, 8)})</Text>
          ) : null}
        </Text>
      ),
    },
    codeColumn('결과', 'result', 'AUDIT_RESULT', 100, codes),
  ],

  login: (scope, codes) => [
    { title: '일시', dataIndex: 'at', width: 160, render: when },
    ...(scope === 'PLATFORM' ? [COMPANY_COLUMN] : []),
    { title: '사용자', width: 130, render: (_, row) => actor(row) },
    codeColumn('결과', 'result', 'LOGIN_RESULT', 130, codes),
    { title: '실패 사유', dataIndex: 'failure_reason', width: 160 },
    { title: 'IP', dataIndex: 'client_ip', width: 140 },
    {
      title: '접속 기기',
      dataIndex: 'user_agent',
      ellipsis: true,
      render: (v: unknown) => <Text type="secondary">{(v as string) ?? ''}</Text>,
    },
  ],

  session: (scope, codes) => [
    { title: '시작', dataIndex: 'at', width: 160, render: when },
    ...(scope === 'PLATFORM' ? [COMPANY_COLUMN] : []),
    { title: '사용자', width: 130, render: (_, row) => actor(row) },
    {
      title: '상태',
      dataIndex: 'live',
      width: 90,
      render: (v: unknown) => (v ? <Tag color="green">접속 중</Tag> : <Tag>종료</Tag>),
    },
    codeColumn('방식', 'login_method', 'LOGIN_METHOD', 110, codes),
    { title: 'IP', dataIndex: 'client_ip', width: 140 },
    { title: '최종 활동', dataIndex: 'last_seen_at', width: 160, render: when },
    codeColumn('종료 사유', 'end_reason', 'SESSION_END_REASON', 140, codes),
  ],

  message: (scope, codes) => [
    { title: '요청', dataIndex: 'at', width: 160, render: when },
    ...(scope === 'PLATFORM' ? [COMPANY_COLUMN] : []),
    codeColumn('채널', 'channel', 'MESSAGE_CHANNEL', 100, codes),
    { title: '템플릿', dataIndex: 'template_code', width: 160 },
    { title: '제목', dataIndex: 'subject', ellipsis: true },
    { title: '수신자', width: 130, render: (_, row) => actor(row) },
    codeColumn('상태', 'status', 'MESSAGE_STATUS', 110, codes),
    { title: '오류', dataIndex: 'error_message', ellipsis: true, width: 180 },
  ],

  alert: (scope, codes) => [
    { title: '발생', dataIndex: 'at', width: 160, render: when },
    ...(scope === 'PLATFORM' ? [COMPANY_COLUMN] : []),
    codeColumn('등급', 'severity', 'ALERT_SEVERITY', 100, codes),
    { title: '규칙', dataIndex: 'rule_name', width: 180 },
    { title: '내용', dataIndex: 'message', ellipsis: true },
    codeColumn('상태', 'status', 'ALERT_STATUS', 110, codes),
  ],

  file: (scope, codes) => [
    { title: '발급', dataIndex: 'at', width: 160, render: when },
    ...(scope === 'PLATFORM' ? [COMPANY_COLUMN] : []),
    codeColumn('구분', 'operation', 'PRESIGN_OPERATION', 100, codes),
    {
      title: '파일',
      ellipsis: true,
      render: (_, row) =>
        (row.fileName as string) ?? (
          <Text type="secondary" style={{ fontFamily: 'monospace' }}>
            {String(row.file_id ?? '').slice(0, 8)}
          </Text>
        ),
    },
    { title: '크기', dataIndex: 'byte_size', width: 100, render: bytes },
    { title: '사용자', width: 130, render: (_, row) => actor(row) },
    { title: 'IP', dataIndex: 'client_ip', width: 140 },
    { title: '용도', dataIndex: 'purpose', width: 110 },
  ],

  setting: (scope) => [
    { title: '변경', dataIndex: 'at', width: 160, render: when },
    ...(scope === 'PLATFORM' ? [COMPANY_COLUMN] : []),
    { title: '설정 키', dataIndex: 'setting_key', width: 220 },
    { title: '범위', dataIndex: 'scope_type', width: 100 },
    { title: '변경자', width: 130, render: (_, row) => actor(row) },
    { title: '사유', dataIndex: 'change_reason', ellipsis: true },
  ],

  webhook: (_scope, codes) => [
    { title: '수신', dataIndex: 'at', width: 160, render: when },
    { title: '제공자', dataIndex: 'provider', width: 110 },
    { title: '이벤트', dataIndex: 'event_type', width: 180 },
    { title: '주문번호', dataIndex: 'merchant_uid', width: 180 },
    {
      title: '서명',
      dataIndex: 'signature_valid',
      width: 90,
      render: (v: unknown) => (v ? <Tag color="green">정상</Tag> : <Tag color="red">불일치</Tag>),
    },
    {
      title: '처리',
      dataIndex: 'process_result',
      width: 110,
      render: (v: unknown) =>
        v ? (
          <CodeTag codes={codes} group="WEBHOOK_RESULT" value={v as string} />
        ) : (
          <Tag color="blue">대기</Tag>
        ),
    },
    { title: '오류', dataIndex: 'error_message', ellipsis: true },
  ],
};

/** 키워드가 무엇을 찾는지는 탭마다 다르다. 자리표시자로 알려 준다. */
const KEYWORD_HINT: Record<string, string> = {
  audit: '대상 이름',
  message: '제목',
  setting: '설정 키',
  webhook: '주문번호',
};

/* =========================================================================
 * 화면
 * ====================================================================== */

export default function LogsPage({ scope }: { scope: LogScope }) {
  const base = scope === 'PLATFORM' ? '/admin/logs' : '/logs';
  const codes = useCodes(CODE_GROUPS);
  const { message } = AntdApp.useApp();
  const [exporting, setExporting] = useState(false);

  const [source, setSource] = useState('audit');
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(6, 'day'), dayjs()]);
  const [keyword, setKeyword] = useState('');
  const [extra, setExtra] = useState<Record<string, string | undefined>>({});
  const [tenantId, setTenantId] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);

  // 탭을 옮기면 이전 탭의 필터가 남아 "왜 아무것도 안 나오지" 가 된다.
  useEffect(() => {
    setExtra({});
    setKeyword('');
    setPage(1);
  }, [source]);

  const sources = useQuery<LogSource[], NormalizedError>({
    queryKey: ['logs', 'sources', scope],
    queryFn: async () => (await apiClient.get<LogSource[]>(`${base}/sources`)).data,
  });

  const tenants = useQuery<TenantOption[], NormalizedError>({
    queryKey: ['logs', 'tenants'],
    enabled: scope === 'PLATFORM',
    queryFn: async () => {
      const rows = (await apiClient.get<TenantOption[]>('/admin/tenants')).data;
      return rows.map((r) => ({ tenantId: r.tenantId, companyName: r.companyName }));
    },
  });

  /*
   * 거르기 칸에 쓸 제품 목록.
   *
   * 지워진 제품의 지난 기록도 찾을 수 있어야 하므로 중지된 제품까지 받는다.
   * 이 목록은 통합관리자만 읽을 수 있어 자사 조회에서는 부르지 않는다 —
   * 「발생 시스템」 칸 자체는 기록에 이름이 함께 와서 양쪽 다 보인다.
   */
  const products = useQuery<ProductOption[], NormalizedError>({
    queryKey: ['logs', 'products'],
    enabled: scope === 'PLATFORM',
    staleTime: 10 * 60 * 1000,
    queryFn: async () =>
      (
        await apiClient.get<ProductOption[]>('/admin/products', {
          params: { includeInactive: true },
        })
      ).data,
  });

  const params = useMemo(
    () => ({
      from: range[0].format('YYYY-MM-DD'),
      to: range[1].format('YYYY-MM-DD'),
      page,
      size,
      ...(keyword ? { keyword } : {}),
      ...(scope === 'PLATFORM' && tenantId ? { tenantId } : {}),
      ...Object.fromEntries(Object.entries(extra).filter(([, v]) => v)),
    }),
    [range, page, size, keyword, extra, tenantId, scope],
  );

  const logs = useQuery<LogPage, NormalizedError>({
    queryKey: ['logs', scope, source, params],
    queryFn: async () => (await apiClient.get<LogPage>(`${base}/${source}`, { params })).data,
  });

  const columns = (COLUMNS[source] ?? COLUMNS.audit!)(scope, codes);

  /**
   * CSV 로 받기.
   *
   * 토큰이 필요해 링크를 그냥 열 수 없다. 본문을 받아 브라우저가 저장하게 한다.
   * 지금 화면에 건 조건(기간·필터)을 그대로 쓴다 — 보고 있는 것과 다른 것이
   * 내려오면 감사 자료로 쓸 수 없다.
   */
  const exportCsv = async () => {
    setExporting(true);
    try {
      const { page: _p, size: _s, ...conditions } = params;
      const response = await apiClient.get(`${base}/export/${source}`, {
        params: conditions,
        responseType: 'blob',
      });

      const rows = Number(response.headers['x-export-rows'] ?? 0);
      if (rows === 0) {
        message.info('이 조건에 해당하는 기록이 없습니다.');
        return;
      }

      const url = URL.createObjectURL(response.data as Blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${source}_${params.from}_${params.to}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);

      if (response.headers['x-export-truncated'] === 'true') {
        message.warning(
          `상한(${rows.toLocaleString()}건)까지만 내려받았습니다. 기간을 나눠 다시 받아 주세요.`,
        );
      } else {
        message.success(`${rows.toLocaleString()}건을 내려받았습니다.`);
      }
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        size="small"
        title="로그 조회"
        extra={
          <Space>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={exporting}
              onClick={() => void exportCsv()}
            >
              CSV 받기
            </Button>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => void logs.refetch()}
              loading={logs.isFetching}
            >
              새로고침
            </Button>
          </Space>
        }
      >
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          {scope === 'PLATFORM' ? (
            <>
              모든 기업의 기록을 봅니다. 구성원 이름·파일명은 풀지 않습니다 — 누가 했는지까지
              확인하려면 「기업 관리」에서 지원 모드로 들어가야 합니다.
            </>
          ) : (
            <>우리 회사 기록만 보입니다. 조회 기간은 최대 92일입니다.</>
          )}
        </Paragraph>

        <Space wrap size="middle">
          <DatePicker.RangePicker
            value={range}
            allowClear={false}
            onChange={(v) => {
              if (v?.[0] && v[1]) {
                setRange([v[0], v[1]]);
                setPage(1);
              }
            }}
            presets={[
              { label: '오늘', value: [dayjs(), dayjs()] },
              { label: '최근 7일', value: [dayjs().subtract(6, 'day'), dayjs()] },
              { label: '최근 30일', value: [dayjs().subtract(29, 'day'), dayjs()] },
              { label: '최근 90일', value: [dayjs().subtract(89, 'day'), dayjs()] },
            ]}
          />

          {scope === 'PLATFORM' && (
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="기업 전체"
              style={{ width: 200 }}
              value={tenantId}
              onChange={(v) => {
                setTenantId(v);
                setPage(1);
              }}
              options={(tenants.data ?? []).map((t) => ({
                value: t.tenantId,
                label: t.companyName,
              }))}
            />
          )}

          {source === 'audit' && scope === 'PLATFORM' && (
            <Select
              allowClear
              placeholder="발생 시스템 전체"
              style={{ width: 180 }}
              value={extra.productCode}
              onChange={(v) => {
                setExtra((prev) => ({ ...prev, productCode: v }));
                setPage(1);
              }}
              options={(products.data ?? []).map((p) => ({
                value: p.productCode,
                label: p.productName,
              }))}
            />
          )}

          {source === 'audit' && (
            <Select
              allowClear
              placeholder="분류 전체"
              style={{ width: 170 }}
              value={extra.category}
              onChange={(v) => {
                setExtra((prev) => ({ ...prev, category: v }));
                setPage(1);
              }}
              options={codes.options('AUDIT_CATEGORY')}
            />
          )}

          {(FILTER_FIELDS[source] ?? []).map((f) => (
            <Select
              key={f.key}
              allowClear
              placeholder={`${f.label} 전체`}
              style={{ width: 150 }}
              value={extra[f.key]}
              onChange={(v) => {
                setExtra((prev) => ({ ...prev, [f.key]: v }));
                setPage(1);
              }}
              options={codes.options(f.group)}
            />
          ))}

          {KEYWORD_HINT[source] && (
            <Input.Search
              allowClear
              placeholder={KEYWORD_HINT[source]}
              style={{ width: 200 }}
              onSearch={(v) => {
                setKeyword(v);
                setPage(1);
              }}
            />
          )}
        </Space>
      </Card>

      {scope === 'PLATFORM' && <RetentionPanel />}

      {logs.isError && <Alert type="error" showIcon message={errorText(logs.error)} />}

      <Card size="small" styles={{ body: { paddingTop: 0 } }}>
        <Tabs
          activeKey={source}
          onChange={setSource}
          items={(sources.data ?? []).map((s) => ({ key: s.key, label: s.label }))}
        />

        <Table<Record<string, unknown>>
          rowKey={(row) => String(row.id)}
          size="small"
          loading={logs.isFetching}
          dataSource={logs.data?.items ?? []}
          columns={columns}
          scroll={{ x: 1100 }}
          locale={{
            emptyText: <Empty description="이 기간에는 기록이 없습니다" />,
          }}
          onRow={(row) => ({ onClick: () => setDetail(row), style: { cursor: 'pointer' } })}
          pagination={{
            current: page,
            pageSize: size,
            total: logs.data?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100, 200],
            // 상한에 걸리면 정확한 수를 모른다. 그대로 적으면 거짓말이 된다.
            showTotal: (total) =>
              logs.data?.totalCapped ? `${total.toLocaleString()}건 이상` : `${total.toLocaleString()}건`,
            onChange: (p, s) => {
              setPage(p);
              setSize(s);
            },
          }}
        />
      </Card>

      <LogDetailDrawer row={detail} onClose={() => setDetail(null)} />
    </Space>
  );
}

/**
 * 보관 기간.
 *
 * 기간 자체는 공통코드(LOG_RETENTION)에서 고친다. 여기서는 지금 적용 중인
 * 값과 남아 있는 양을 보여 주고, 필요하면 그 자리에서 한 번 돌린다.
 * 평소에는 매일 새벽 3시 30분에 자동으로 돈다.
 */
function RetentionPanel() {
  const { message } = AntdApp.useApp();
  const client = useQueryClient();
  const [running, setRunning] = useState(false);

  const status = useQuery<RetentionStatus[], NormalizedError>({
    queryKey: ['admin', 'logs', 'retention'],
    queryFn: async () =>
      (await apiClient.get<RetentionStatus[]>('/admin/logs/retention')).data,
  });

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await apiClient.post<Array<{ removed: number; error?: string }>>(
        '/admin/logs/retention/run',
      );
      const removed = data.reduce((sum, r) => sum + r.removed, 0);
      const failed = data.filter((r) => r.error);
      if (failed.length > 0) message.warning(`${removed}건 정리, ${failed.length}종 실패`);
      else message.success(`${removed}건을 정리했습니다.`);
      void client.invalidateQueries({ queryKey: ['admin', 'logs', 'retention'] });
      void client.invalidateQueries({ queryKey: ['logs'] });
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card
      size="small"
      title="보관 기간"
      extra={
        <Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            매일 03:30 자동 정리
          </Text>
          <Link to="/app/codes">
            <Button size="small" type="link">
              기간 변경
            </Button>
          </Link>
          <Popconfirm
            title="지금 정리할까요?"
            description="보관 기간이 지난 기록을 지웁니다. 되돌릴 수 없습니다."
            okText="정리"
            cancelText="취소"
            onConfirm={() => void run()}
          >
            <Button size="small" danger loading={running}>
              지금 정리
            </Button>
          </Popconfirm>
        </Space>
      }
    >
      <Table<RetentionStatus>
        rowKey="code"
        size="small"
        pagination={false}
        loading={status.isLoading}
        dataSource={status.data ?? []}
        columns={[
          { title: '로그', dataIndex: 'label', width: 160 },
          {
            title: '보관',
            dataIndex: 'days',
            width: 110,
            render: (v: number) => `${v.toLocaleString()}일`,
          },
          {
            title: '남아 있는 양',
            dataIndex: 'rows',
            width: 130,
            render: (v: number | null) =>
              v === null ? (
                <Text type="secondary">—</Text>
              ) : (
                <Text type="secondary">약 {v.toLocaleString()}건</Text>
              ),
          },
          {
            title: '가장 오래된 기록',
            dataIndex: 'oldest',
            render: (v: string | null, row) =>
              v ? (
                <Space size={6}>
                  <Text>{dayjs(v).format('YYYY-MM-DD')}</Text>
                  {dayjs(v).isBefore(dayjs(row.cutoff)) && (
                    <Tag color="orange">정리 대상 있음</Tag>
                  )}
                </Space>
              ) : (
                <Text type="secondary">없음</Text>
              ),
          },
        ]}
      />
    </Card>
  );
}

/**
 * 한 줄 상세.
 *
 * 목록에 담기지 않는 값(변경 전후, 맥락 JSON, User-Agent 전문)을 보여 준다.
 * 표에 다 넣으면 가로로 끝없이 늘어나 정작 훑어보기가 어려워진다.
 */
function LogDetailDrawer({
  row,
  onClose,
}: {
  row: Record<string, unknown> | null;
  onClose: () => void;
}) {
  const json = (value: unknown) =>
    value === null || value === undefined ? null : (
      <pre
        style={{
          margin: 0,
          padding: 8,
          background: '#fafafa',
          borderRadius: 4,
          maxHeight: 280,
          overflow: 'auto',
          fontSize: 12,
        }}
      >
        {JSON.stringify(value, null, 2)}
      </pre>
    );

  // 표에 이미 있거나 따로 그리는 값은 아래 목록에서 뺀다.
  const HIDDEN = new Set([
    'id',
    'before_data',
    'after_data',
    'context',
    'payload',
    'tenant_id',
  ]);

  const entries = Object.entries(row ?? {}).filter(
    ([key, value]) => !HIDDEN.has(key) && value !== null && value !== undefined && value !== '',
  );

  return (
    <Drawer open={Boolean(row)} onClose={onClose} width={640} title="로그 상세">
      {row && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Descriptions bordered size="small" column={1}>
            {entries.map(([key, value]) => (
              <Descriptions.Item key={key} label={key}>
                {typeof value === 'object' ? (
                  json(value)
                ) : key.endsWith('_at') || key === 'at' ? (
                  when(value)
                ) : (
                  <Text style={{ wordBreak: 'break-all' }}>{String(value)}</Text>
                )}
              </Descriptions.Item>
            ))}
          </Descriptions>

          {row.before_data ? (
            <Card size="small" title="변경 전">
              {json(row.before_data)}
            </Card>
          ) : null}
          {row.after_data ? (
            <Card size="small" title="변경 후">
              {json(row.after_data)}
            </Card>
          ) : null}
          {row.context && Object.keys(row.context as object).length > 0 ? (
            <Card size="small" title="맥락">
              {json(row.context)}
            </Card>
          ) : null}
          {row.payload ? (
            <Card size="small" title="수신 본문">
              {json(row.payload)}
            </Card>
          ) : null}
        </Space>
      )}
    </Drawer>
  );
}
